import type { BankAccount, ProjectFormValues } from "../../types";

/**
 * The subset of the store's `Project` type that the Liquid template engine
 * receives at the root of its render context. Mirrors
 * `store/types/Project.ts` field-for-field. Adding a field here means the
 * AI-prompt template-author guide should also be updated.
 */
export interface StoreProjectShape {
  id: string;
  slug?: string;
  title: string;
  description: string;
  projectDetails: string;
  projectDescriptionImagesUrls: string[];
  shortDescription: string;
  imageUrl: string;
  imageAlt?: string;
  bannerImageFullUrl: string;
  progress: number;
  displayProgress: boolean;
  allRequiredAmount: number;
  currentPaidAmount: number;
  projectType: number;
  projectGrades: Array<{
    id: number;
    order: number;
    title: string;
    amount: number;
    deductionPeriod: number | null;
    deductionEndTime: string | null;
    numberOfBeneficiariesNames: number;
  }>;
  projectUnitValue: number;
  allowOpenDonation: boolean;
  minimumOpenDonationAmount: number;
  allowCustomAmount: boolean;
  isOnBehalfOnProject: boolean;
  hasEndowmentCertificate: boolean;
  isFastPaymentActive: boolean;
  linkedGiftingProjectId: string;
  projectVisualEffect: boolean;
  projectBankAccountShowingOption: number;
  projectBankAccounts: Array<{
    iban: string;
    bankName: string;
    bankAccountId: number;
    bankAccountName: string;
  }>;
  shareOnTwitter: boolean;
  shareOnFacebook: boolean;
  shareOnInstagram: boolean;
  shareOnWhatsApp: boolean;
  seoTitle: string;
  seoDescription: string;
  seoKeyword: string;
}

/**
 * Translates the admin's `ProjectFormValues` shape into the store's
 * `Project` shape used by the Liquid renderer. Pure function — give it the
 * current form snapshot, the resolved bank-account list (so it can expand
 * `bankAccountsIds` into full account info), and the candidate template
 * string. Returns the project object the renderer consumes.
 *
 * Field mapping notes:
 *   - `projectAttributes` → `projectGrades` (admin terminology vs store)
 *   - `projectAccountShowingOption` → `projectBankAccountShowingOption`
 *   - `showProgressBar` → `displayProgress`
 *   - `haveCertificate` → `hasEndowmentCertificate`
 *   - `giftProjectId` (number) → `linkedGiftingProjectId` (string)
 *   - `seoKeywords` (plural) → `seoKeyword` (singular, store's spelling)
 *   - `progress` is computed from currentPaidAmount / allRequiredAmount
 *   - `projectUnitValue` is not on the admin form — defaulted to 0
 *   - Per-platform share toggles aren't on the admin form — defaulted to true
 *     so templates that gate rendering on them show all share buttons.
 */
export function mapFormToStoreProject(
  form: ProjectFormValues,
  banks: BankAccount[],
): StoreProjectShape {
  const projectType =
    typeof form.projectType === "string"
      ? Number(form.projectType)
      : form.projectType;

  const requiredAmount = Number(form.allRequiredAmount) || 0;
  const paidAmount = Number(form.currentPaidAmount) || 0;
  const progress =
    requiredAmount > 0
      ? Math.min(100, Math.round((paidAmount / requiredAmount) * 100))
      : 0;

  return {
    id: String(form.id ?? "preview"),
    slug: form.slug,
    title: form.title || form.name,
    description: form.projectDetails,
    projectDetails: form.projectDetails ?? "",
    projectDescriptionImagesUrls: [],
    shortDescription: form.shortDescription,
    imageUrl: form.imageUrl || "",
    imageAlt: form.imageAlt,
    bannerImageFullUrl: form.imageUrl || "",
    progress,
    displayProgress: Boolean(form.showProgressBar),
    allRequiredAmount: requiredAmount,
    currentPaidAmount: paidAmount,
    projectType: Number.isFinite(projectType) ? Number(projectType) : 0,
    projectGrades: (form.projectAttributes ?? []).map((attr, idx) => ({
      id: attr.id ?? idx + 1,
      order: attr.order ?? idx,
      title: attr.title ?? "",
      amount: Number(attr.price) || 0,
      deductionPeriod: attr.duration ? Number(attr.duration) : null,
      deductionEndTime: attr.endingTime ?? null,
      numberOfBeneficiariesNames: attr.numberOfBeneficiariesNames ?? 0,
    })),
    projectUnitValue: 0,
    allowOpenDonation: Boolean(form.allowOpenDonation),
    minimumOpenDonationAmount: Number(form.minimumOpenDonationAmount) || 0,
    allowCustomAmount: Boolean(form.allowOpenDonation),
    isOnBehalfOnProject: Boolean(form.isOnBehalfOnProject),
    hasEndowmentCertificate: Boolean(form.haveCertificate),
    isFastPaymentActive: Boolean(form.isFastPaymentActive),
    linkedGiftingProjectId: form.giftProjectId
      ? String(form.giftProjectId)
      : "",
    projectVisualEffect: Boolean(form.projectVisualEffect),
    projectBankAccountShowingOption:
      Number(form.projectAccountShowingOption) || 0,
    projectBankAccounts: (form.bankAccountsIds ?? [])
      .map((id) => banks.find((b) => b.id === id))
      .filter((b): b is BankAccount => Boolean(b))
      .map((b) => ({
        iban: b.iban,
        bankName: b.bankName ?? "",
        bankAccountId: b.id,
        bankAccountName: b.name,
      })),
    shareOnTwitter: true,
    shareOnFacebook: true,
    shareOnInstagram: true,
    shareOnWhatsApp: true,
    seoTitle: form.seoTitle || form.title || "",
    seoDescription: form.seoDescription || form.shortDescription || "",
    seoKeyword: form.seoKeywords || "",
  };
}
